import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { fetchSettings } from "./redux/usersSagas";
import { usePopup } from "./hooks/usePopup";
import { Add } from "./smartComponents/Add";
import { Edit } from "./smartComponents/Edit";
import { Delete } from "./smartComponents/Delete";
import FilterUsers from "./smartComponents/FilterUsers";
import { Duplicate } from "./smartComponents/Duplicate";
import { Mirror } from "./smartComponents/Mirror";
import { LoginAs } from "./smartComponents/LoginAs";
import { AddAdmin } from "./smartComponents/AddAdmin";
import { Upload } from "./smartComponents/Upload";
import { Reactivate } from "./smartComponents/Reactivate";
import { SetAdmin } from "./smartComponents/SetAdmin";
import { TagsList } from "./smartComponents/Tags";
import { UsersSettingsMenu } from "./smartComponents/UsersSettingsMenu";
import { menuActions } from "./api";
import { UserRow } from "./smartComponents/UserRow";
import LoadingAnimation from "../../../components/loadingAnimation/loadingAnimation";
import { WindowVirtualList } from "../../../components/WindowVirtualList/WindowVirtualList";
import { requestWithToast, requestWithDownload } from "../../../utils/request";
import "./users.scss";

function prepareListItems(users, searchValue) {
  const rows = [];
  if (!users) return rows;

  Object.entries(users).forEach(([orgName, userList]) => {
    const filteredUsers = !searchValue
      ? userList
      : userList.filter(
          (user) =>
            user.firstName.toLowerCase().includes(searchValue) ||
            user.lastName.toLowerCase().includes(searchValue)
        );

    if (filteredUsers.length > 0) {
      rows.push({ type: "header", orgName, usersCount: filteredUsers.length });

      filteredUsers.forEach((user) => {
        rows.push({ type: "user", orgName, user });
      });
    }
  });

  return rows;
}

export function UsersModule({ params }) {
  const dispatch = useDispatch();
  const users = useSelector((state) => state.users.users);
  const isLoading = useSelector((state) => state.users.isLoading);
  const areUsersLoading = useSelector((state) => state.users.areUsersLoading);
  const [selectedUser, setSelectedUser] = useState("");
  const [userEmailId, setUserEmailId] = useState(null);
  const { popup, openPopup, closePopup } = usePopup();

  useEffect(() => {
    dispatch(fetchSettings(params));
  }, []);

  const onMenuItemClick = ({ value, syntheticEvent }, item = {}) => {
    syntheticEvent.preventDefault();

    let { type, endpoint, requiresUser } = menuActions[value];

    if (type === "request") {
      if (value === "exportActiveUsers") {
        requestWithDownload(endpoint, "active_users");
        return;
      }

      requestWithToast(endpoint, { id: item.id.toString() });
    }

    if (type === "popup") {
      if (requiresUser) {
        setSelectedUser(item);
      }
      openPopup(value);
    }
  };

  const handlePopup = (item, type) => {
    setSelectedUser(item);

    openPopup(type);
  };

  const handleEmails = (id) => {
    setUserEmailId(id === userEmailId ? null : id);
  };

  if (isLoading)
    return (
      <div className="light">
        <LoadingAnimation uiLoader />
      </div>
    );

  return (
    <div id="admin-users">
      <div className="admin-users">
        <div className="admin-users__menu">
          <UsersSettingsMenu onMenuItemClick={onMenuItemClick} />
          <TagsList />
        </div>

        <div className="admin-users__top">
          <FilterUsers />
        </div>

        <div className="admin-users__content">
          <ul className="group">
            {areUsersLoading ? (
              <LoadingAnimation />
            ) : users && Object.keys(users).length ? (
              <>
                <p style={{ fontWeight: "bold", marginBottom: 20, fontSize: "22px" }}>
                  Total Users:{" "}
                  {Object.values(users).reduce((acc, userList) => acc + userList.length, 0)}
                </p>
                <WindowVirtualList
                  rawItems={users}
                  processItemsFn={prepareListItems}
                  estimateSize={() => 40}
                  renderRow={({ item }) => (
                    <UserRow
                      item={item}
                      handleEmails={handleEmails}
                      onMenuItemClick={onMenuItemClick}
                      handlePopup={handlePopup}
                      userEmailId={userEmailId}
                    />
                  )}
                />
              </>
            ) : null}
            {users && Object.keys(users).length === 0 && (
              <p className="error error--medium">No users found</p>
            )}
          </ul>
        </div>
        {popup.addUser && <Add onClose={closePopup("addUser")} />}
        {popup.addAdminUser && <AddAdmin onClose={closePopup("addAdminUser")} />}
        {popup.bulkUsersCreation && <Upload onClose={closePopup("bulkUsersCreation")} />}

        {popup.edit && (
          <Edit isOpen={popup.edit} onClose={closePopup("edit")} selected={selectedUser} />
        )}

        {popup.setAdmin && (
          <SetAdmin
            isOpen={popup.setAdmin}
            onClose={closePopup("setAdmin")}
            selected={{
              id: selectedUser.id,
              name: selectedUser.name,
              orgName: selectedUser.orgName
            }}
          />
        )}

        {popup.duplicate && (
          <Duplicate
            isOpen={popup.duplicate}
            onClose={closePopup("duplicate")}
            selected={{
              id: selectedUser.id,
              name: selectedUser.name,
              orgName: selectedUser.orgName
            }}
          />
        )}

        {popup.mirror && (
          <Mirror
            isOpen={popup.mirror}
            onClose={closePopup("mirror")}
            selected={{
              id: selectedUser.id,
              name: selectedUser.name,
              organizationId: selectedUser.organizationId,
              orgName: selectedUser.orgName
            }}
          />
        )}

        {popup.delete && (
          <Delete
            isOpen={popup.delete}
            onClose={closePopup("delete")}
            selected={{
              id: selectedUser.id,
              name: selectedUser.name,
              isDeleted: selectedUser.statuses.isDeleted
            }}
          />
        )}

        {popup.reactivate && (
          <Reactivate
            isOpen={popup.reactivate}
            onClose={closePopup("reactivate")}
            selected={{
              id: selectedUser.id,
              name: selectedUser.name
            }}
          />
        )}

        {popup.loginAs && (
          <LoginAs
            isOpen={popup.loginAs}
            onClose={closePopup("loginAs")}
            selected={{ id: selectedUser.id }}
          />
        )}
      </div>
    </div>
  );
}
